import { nowLocal } from '@app/shared/utils/date.utils';
import { getApiBaseUrl } from '@app/shared/utils/env.utils';
import { useQuotaErrorStore } from '@app/shared/store/quota-error.store';
import { errorCodeMap } from '@app/shared/utils/error-message.utils';
import { i18next } from '@i18n/i18n';
import { addBreadcrumb } from '../../../sentry-client';
import axios, {
  AxiosInstance,
  AxiosProgressEvent,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { useAuthStore } from '../store/auth.store';
import { useBranchStore } from '../store/branch.store';
import { useImpersonationStore } from '../store/impersonation.store';
import { useLockStore } from '../store/lock.store';
import { useClockDriftStore } from '../store/clock-drift.store';
import { suppressGenericErrorToasts } from '../utils/toast.utils';
import { toastErrorOnce } from '../utils/toast-once.utils';
import { recoverFromBranchAccessDenied } from '../utils/branch-access-denied.util';
import {
  clearTokenRefresh,
  scheduleTokenRefresh,
} from '../utils/token-refresh-timer';
import {
  adoptRotatedSession,
  coordinatedRefresh,
  subscribeToAuthRotations,
} from '../utils/auth-refresh-coordinator';

import { ErrorCode } from '@neogeniux/api-contracts';
import type { ApiError, PaginatedResponse } from '@neogeniux/api-contracts';
export type {
  ApiError,
  ApiResponse,
  PaginatedResponse,
} from '@neogeniux/api-contracts';

// Request retry configuration
interface RetryConfig {
  retries: number;
  retryDelay: number;
  retryCondition?: (error: unknown) => boolean;
}

// Extended config type with metadata
interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  metadata?: { startTime: number };
  _retry?: boolean;
  _retryCount?: number;
  // Idempotency key stamped on the FIRST attempt and preserved
  // across every retry so the backend content-hash dedupe sees the
  // same key + payload and returns the same invoice.
  _idempotencyKey?: string;
}

class ApiService {
  private axiosInstance: AxiosInstance;

  // Silent refresh state
  private isRefreshing = false;
  private failedQueue: {
    resolve: (token: string) => void;
    reject: (error: unknown) => void;
  }[] = [];

  // Client-side concurrency ceiling — caps the number of /api/* GETs
  // in flight at once. Under an impersonation remount the private
  // tree fires ~15 parallel queries against a 14-slot Supabase
  // session pool, tripping EMAXCONNSESSION across dashboard +
  // settings + subscription surfaces. Cap at 8 in-flight leaves
  // clear headroom for cron / webhook / smoke traffic; excess
  // requests await a queued slot. Mutations bypass the semaphore
  // (user-triggered writes must not be re-ordered behind a burst).
  private static readonly REQUEST_CONCURRENCY_LIMIT = 8;
  private inFlightCount = 0;
  private readonly requestQueue: Array<() => void> = [];

  private async acquireSlot(): Promise<void> {
    if (this.inFlightCount < ApiService.REQUEST_CONCURRENCY_LIMIT) {
      this.inFlightCount += 1;
      return;
    }
    await new Promise<void>((resolve) => {
      this.requestQueue.push(resolve);
    });
    this.inFlightCount += 1;
  }

  private releaseSlot(): void {
    this.inFlightCount = Math.max(0, this.inFlightCount - 1);
    const next = this.requestQueue.shift();
    if (next) next();
  }

  private readonly defaultRetryConfig: RetryConfig = {
    retries: 3,
    retryDelay: 1000,
    retryCondition: (error: unknown) => {
      const axiosError = error as {
        response?: { status?: number };
        code?: string;
      };
      // Online-only retry. If the browser knows we're offline, skip
      // retries — three back-offs in a row just make the UI hang for
      // ~9 s before the error surfaces. Same reasoning for
      // ERR_CANCELED: the caller asked us to stop.
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return false;
      }
      if (axiosError.code === 'ERR_CANCELED') return false;
      return (
        !axiosError.response ||
        (axiosError.response.status !== undefined &&
          axiosError.response.status >= 500) ||
        axiosError.code === 'NETWORK_ERROR'
      );
    },
  };

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: this.getBaseURL(),
      // 15 s ceiling. Earlier 30 s timeout combined with 3 retries gave
      // a worst-case ~60 s hang before the caller saw an error, which
      // is what made the payment modal feel "stuck" on a dropped
      // connection. 15 s + offline-aware retry is well under the modal
      // safety race.
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
    this.setupMultiTabSync();
  }

  private getBaseURL(): string {
    return getApiBaseUrl();
  }

  private isEcfEndpoint(url?: string): boolean {
    if (!url) return false;
    const normalized = url.startsWith('/') ? url : `/${url}`;
    return normalized.includes('/ecf/');
  }

  /**
   * Endpoints that create invoices (or invoice-shaped documents:
   * credit notes, bulk imports, pending-payment finalizes). Every
   * POST here must:
   *   1. Carry a stable `X-Idempotency-Key` so the backend content-
   *      hash dedupe recognizes retries.
   *   2. NEVER be auto-retried by axios — retry-after-500 is the
   *      dominant duplicate-invoice source in production, so the
   *      operator must explicitly click again from the UI.
   */
  private isInvoiceCreateEndpoint(url?: string, method?: string): boolean {
    if (!url) return false;
    if (String(method ?? '').toLowerCase() !== 'post') return false;
    const normalized = url.startsWith('/') ? url : `/${url}`;
    return (
      normalized === '/invoices' ||
      normalized.endsWith('/invoices/') ||
      normalized.includes('/invoices/bulk-import') ||
      /\/invoices\/[^/]+\/finalize$/.test(normalized) ||
      normalized === '/credit-notes' ||
      normalized.endsWith('/credit-notes/')
    );
  }

  /**
   * Endpoints that are inherently business-scoped (once per tenant,
   * NOT per branch). The auto-inject below adds `branchOfficeId` +
   * `X-Branch-Office-Id` to every authed request, which fragments
   * the React Query cache per-branch and forces useless refetches
   * on branch switch even though the response is identical.
   */
  private isBusinessScopedEndpoint(url?: string): boolean {
    if (!url) return false;
    const normalized = url.startsWith('/') ? url : `/${url}`;
    return (
      normalized.includes('/contracts/me') ||
      normalized.includes('/contracts/current')
    );
  }

  private isAuthEndpoint(url?: string): boolean {
    if (!url) return false;

    // `config.url` can be:
    // - relative with leading slash: "/auth/login"
    // - relative without leading slash (after normalization): "auth/login"
    // - absolute: "https://.../api/auth/login"
    const normalized = url.startsWith('/') ? url : `/${url}`;
    return (
      normalized.includes('/auth/login') ||
      normalized.includes('/auth/refresh') ||
      normalized.includes('/auth/logout') ||
      normalized.includes('/auth/logout-all')
    );
  }

  /**
   * Endpoints backed by the backend `assertSuperAdmin` gate — they
   * fail with 403 when called with an impersonated user's token. The
   * request interceptor forces the sadmin's original token onto every
   * hit here while `isImpersonating` is true, so a sadmin panel that
   * stays mounted (or is opened) during impersonation keeps working.
   */
  private isSadminScopedEndpoint(url?: string): boolean {
    if (!url) return false;
    const normalized = url.startsWith('/') ? url : `/${url}`;
    return (
      normalized.includes('/sadmin/') ||
      normalized.includes('/subscription/business/') ||
      // Chained impersonation: firing /auth/impersonate/:userId from
      // an already-impersonated session must send the sadmin JWT, not
      // the current impersonated user's. Backend re-checks role by
      // JWT.sub, so a non-sadmin token 403s with "You do not have
      // permission to impersonate users".
      normalized.includes('/auth/impersonate/') ||
      // Every /business/admin-* + /auth/admin/* endpoint gates on
      // `assertSuperAdmin` — the SAME class as `/sadmin/*`. They must
      // always ride the sadmin token, otherwise a lingering
      // impersonated JWT (either from an in-flight refetch that fired
      // during the Retornar swap OR from an axios instance still
      // wired against the old auth-store snapshot) reaches the
      // backend and gets 403 "Only super admins can access this
      // resource" — visible on the /sadmin/businesses landing page
      // (2026-08-14 report). Sadmin-scoping these forces the
      // interceptor to resolve the sadmin token via
      // `sadminFromImpersonation ?? sadminFromAuth ?? authToken`.
      normalized.includes('/business/admin-') ||
      normalized.includes('/auth/admin/')
    );
  }

  /**
   * Endpoints that must remain callable while the screen is locked —
   * everything else the InactivityLock flow needs to verify the
   * operator and let them back in. Any other request fired while
   * locked (dashboard polling, business settings, permissions epoch
   * checks, etc.) is aborted at the request layer so the network
   * stays cold until unlock.
   */
  private isUnlockAllowedEndpoint(url?: string): boolean {
    if (!url) return false;
    const normalized = url.startsWith('/') ? url : `/${url}`;
    return (
      normalized.includes('/auth/login') ||
      normalized.includes('/auth/refresh') ||
      normalized.includes('/auth/logout') ||
      normalized.includes('/auth/pin/verify') ||
      normalized.includes('/auth/card/login')
    );
  }

  /** Access token the request was SENT with (null when it carried none). */
  private getBearerToken(config: ExtendedAxiosRequestConfig): string | null {
    const headers = config.headers as
      | (Record<string, unknown> & { get?: (name: string) => unknown })
      | undefined;
    const raw =
      (typeof headers?.get === 'function'
        ? headers.get('Authorization')
        : undefined) ?? headers?.Authorization;
    return typeof raw === 'string' && raw.startsWith('Bearer ')
      ? raw.slice('Bearer '.length)
      : null;
  }

  private setBearerToken(
    config: ExtendedAxiosRequestConfig,
    token: string
  ): void {
    config.headers = config.headers || {};
    (
      config.headers as Record<string, unknown>
    ).Authorization = `Bearer ${token}`;
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        // Client-side concurrency gate. Read GETs (idempotent) await
        // a queue slot when > REQUEST_CONCURRENCY_LIMIT are in
        // flight. Mutations (POST / PATCH / PUT / DELETE) bypass —
        // user-triggered writes must not be re-ordered. See slot
        // helpers above.
        const method = (config.method ?? 'get').toLowerCase();
        if (method === 'get' || method === 'head') {
          await this.acquireSlot();
          (config as { _slotAcquired?: boolean })._slotAcquired = true;
        }

        // IMPORTANT: When axios `baseURL` includes a path segment (e.g. ".../api"),
        // providing a request URL that starts with '/' will drop that path.
        // Example: baseURL=https://api.neogeniux.com/api + url=/system-options
        // becomes https://api.neogeniux.com/system-options (WRONG).
        // Normalize to a relative URL so the '/api' prefix is preserved.
        if (typeof config.url === 'string' && config.url.startsWith('/')) {
          config.url = config.url.slice(1);
        }

        // Hard gate: while the screen is locked, only unlock-related
        // requests are allowed through. Dashboard polling, business
        // settings, permissions, etc. all get aborted here regardless
        // of which hook fired them. Avoids the race where a query
        // queued before lock fires after focusManager pause kicks in.
        const isLocked = useLockStore.getState().isLocked;
        if (isLocked && !this.isUnlockAllowedEndpoint(config.url)) {
          const controller = new AbortController();
          controller.abort();
          config.signal = controller.signal;
        }

        // Sadmin-scoped endpoints (`/sadmin/*` + `/subscription/business/:id/*`
        // + `/auth/impersonate/`) MUST always ride the sadmin's own token,
        // never the impersonated user's — otherwise the backend
        // `assertSuperAdmin` gate rejects requests fired from a sadmin
        // panel that stayed mounted after an impersonation swap.
        //
        // Two paths to the sadmin token:
        //   1. Currently impersonating → the stored `originalToken`.
        //   2. Auth store holds a super_admin user → its own token.
        //
        // Historically we only checked (1), which meant a session with
        // a partially-corrupted impersonation store (e.g. the store was
        // cleared but auth still held an impersonated token from a prior
        // session) 403'd on the next impersonate click. Checking (2) as
        // a fallback keeps the flow stable across state hiccups.
        const impersonationState = useImpersonationStore.getState();
        const authState = useAuthStore.getState();
        const authToken = authState.token;
        const authUserRole = (() => {
          const r = authState.user?.role as
            | { name?: string }
            | string
            | undefined;
          return typeof r === 'string' ? r : r?.name;
        })();
        const sadminScoped = this.isSadminScopedEndpoint(config.url);
        const sadminFromImpersonation =
          impersonationState.isImpersonating &&
          !!impersonationState.originalToken
            ? impersonationState.originalToken
            : null;
        const sadminFromAuth =
          authUserRole === 'super_admin' && authToken ? authToken : null;
        const token = sadminScoped
          ? sadminFromImpersonation ?? sadminFromAuth ?? authToken
          : authToken;
        if (token && !this.isAuthEndpoint(config.url)) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Send client's local ISO timestamp with timezone offset embedded.
        // One header serves two backend purposes:
        //  1. ClientTimeMiddleware: parsed as UTC Date for create/update timestamp defaults
        //  2. DateTimezoneInterceptor: offset extracted to anchor date-only filter
        //     strings (e.g. "2026-03-22") to the user's local midnight
        config.headers = config.headers || {};
        config.headers['X-Client-Time'] = nowLocal();
        // IANA timezone (e.g. "America/Santo_Domingo", "Asia/Tokyo").
        // Server uses this for DISPLAY-only formatting (audit
        // timestamps shown to the operator in their local time).
        // Storage stays UTC; lying about the TZ does not move any
        // stored value.
        try {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (tz) config.headers['X-Client-Tz'] = tz;
        } catch {
          // browser without Intl support — fine, server falls back
        }

        // Inject selected branch office ID into every authenticated request
        // so the backend can scope data to the selected branch.
        // 'all' or empty means no branch filter (admin sees all branches).
        // Also propagate it as the X-Branch-Office-Id header so the
        // PermissionGuard + menu service can resolve the user's
        // per-branch role (user_branch_role).
        if (
          !this.isAuthEndpoint(config.url) &&
          !this.isBusinessScopedEndpoint(config.url)
        ) {
          const branchState = useBranchStore.getState();
          const branchOfficeId = branchState.selectedBranchOfficeId;
          // Cross-tenant guard: the persisted `selectedBranchOfficeId`
          // survives F5 across identity changes (impersonation swap,
          // sadmin logout→login into a different tenant). If the UUID
          // isn't in the currently-loaded `branchOffices` for the
          // authenticated user's business, refuse to inject it —
          // otherwise the backend `assertBranchOwnership` throws 403
          // on the very first request and the whole page fails to
          // hydrate. `all` and empty always pass (no filter).
          const currentBusinessId =
            useAuthStore.getState().user?.business?.id ??
            useAuthStore.getState().user?.businessId ??
            '';
          const branchList = branchState.branchOffices;
          // STRICT tenant match — see comment in branch.store.ts.
          // Empty currentBusinessId or any branch row missing/mismatched
          // `businessId` marks the persisted list as stale so we skip
          // injecting the branch on the wire. F5 hydrates branch store
          // before auth store; sending the stale UUID during that
          // window is what triggers the 403.
          const branchesMatchCurrentTenant =
            branchList.length > 0 &&
            !!currentBusinessId &&
            branchList.every(
              (b) => !!b.businessId && b.businessId === currentBusinessId
            );
          const branchInList =
            !branchOfficeId ||
            branchOfficeId === 'all' ||
            (branchesMatchCurrentTenant &&
              branchList.some((b) => b.id === branchOfficeId));
          if (branchOfficeId && branchOfficeId !== 'all' && branchInList) {
            // Skip the auto-inject when the caller already set
            // `branchOfficeId` explicitly — either as a URL string or
            // via `config.params`. Otherwise axios serializes BOTH,
            // producing `?branchOfficeId=foo&branchOfficeId=bar` which
            // the backend parses as an array (or rejects when the
            // first value is a non-UUID sentinel like `'business'`).
            const urlHasParam = !!config.url?.includes('branchOfficeId=');
            // Only treat `config.params.branchOfficeId` as "explicit"
            // when the caller passed a truthy value. Many report
            // services declare `branchOfficeId?: string` on their
            // query type and spread the whole state — landing an
            // `undefined` value on the object. That used to trip
            // the `'branchOfficeId' in params` check and skip the
            // inject, so the backend saw `branchOfficeId: null` and
            // fell back to consolidated view even when the sidebar
            // branch selector was on a specific sucursal.
            const paramsHasParam =
              !!config.params &&
              config.params.branchOfficeId !== undefined &&
              config.params.branchOfficeId !== null &&
              config.params.branchOfficeId !== '';
            if (!urlHasParam && !paramsHasParam) {
              config.params = { ...config.params, branchOfficeId };
            }
            config.headers['X-Branch-Office-Id'] = branchOfficeId;
          }
        }

        // Add metadata for timing
        (config as ExtendedAxiosRequestConfig).metadata = {
          startTime: Date.now(),
        };

        // Stamp / preserve idempotency key on invoice-creating POSTs.
        // Backend `InvoiceDedupeService` uses `X-Idempotency-Key` as a
        // secondary dedupe alongside the content-hash. Persist on
        // `config._idempotencyKey` so any retry (silent-refresh replay,
        // deploy-swap 5xx retry, caller re-fire under the same config)
        // reuses the SAME key — otherwise every retry is a fresh key
        // and the dedupe collapses to content-hash only.
        const cfg = config as ExtendedAxiosRequestConfig;
        if (this.isInvoiceCreateEndpoint(cfg.url, cfg.method)) {
          if (!cfg._idempotencyKey) {
            // Prefer caller-supplied header if present (payment modal
            // already generates + sends one per open). Fall back to a
            // fresh UUID scoped to this request lifetime.
            const existing =
              (cfg.headers as Record<string, string> | undefined)?.[
                'X-Idempotency-Key'
              ] ||
              (cfg.headers as Record<string, string> | undefined)?.[
                'x-idempotency-key'
              ];
            cfg._idempotencyKey =
              existing ||
              (typeof crypto !== 'undefined' && 'randomUUID' in crypto
                ? crypto.randomUUID()
                : `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`);
          }
          config.headers = config.headers || {};
          (config.headers as Record<string, string>)['X-Idempotency-Key'] =
            cfg._idempotencyKey;
        }

        if (process.env.NODE_ENV === 'development') {
          // Rich request log — captures the store state each request
          // was fired under so we can diagnose "why did this hook fire
          // twice?" and prove wave-reduction after auth/branch context
          // fixes. See docs/rules/005-performance.md + the SOL
          // CARIBEÑO impersonation incident.
          const authState = useAuthStore.getState();
          const branchState = useBranchStore.getState();
          const authed = authState.isAuthenticated ? 'Y' : 'N';
          const bid = authState.user?.businessId ?? '-';
          const branch = branchState.selectedBranchOfficeId || '-';
          const listLen = branchState.branchOffices.length;
          console.log(
            `🚀 [req] ${config.method?.toUpperCase()} ${
              config.url
            } auth=${authed} biz=${bid} branch=${branch} list=${listLen}`
          );
        }

        return config;
      },
      (error: unknown) => {
        console.error('❌ Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        const config = response.config as ExtendedAxiosRequestConfig & {
          _slotAcquired?: boolean;
        };
        if (config._slotAcquired) {
          this.releaseSlot();
          config._slotAcquired = false;
        }
        const duration = Date.now() - (config.metadata?.startTime || 0);
        if (process.env.NODE_ENV === 'development') {
          console.log(
            `✅ API Response: ${config.method?.toUpperCase()} ${
              config.url
            } (${duration}ms)`
          );
        }
        // Compare the authoritative server clock against this device's
        // `Date.now()`. The store keeps the latest measurement so any
        // surface (banner, button gate) can read it without an extra
        // round trip.
        const serverTimeHeader = response.headers?.['x-server-time'] as
          | string
          | undefined;
        if (serverTimeHeader) {
          const serverMs = Date.parse(serverTimeHeader);
          if (!Number.isNaN(serverMs)) {
            useClockDriftStore.getState().record(serverMs);
          }
        }
        // `X-Permissions-Epoch` mirrors `business.permissions_epoch`.
        // When it changes vs. the value cached in localStorage the
        // menu / permission caches are stale (a migration or a UI
        // grant/revoke landed). Fire the global event so
        // `usePermissionsEpochListener` re-fetches /menu/user + kills
        // permission queries. Without this hop the persisted Zustand
        // `menu` store keeps the old order forever until the operator
        // logs out manually.
        const epochHeader = response.headers?.['x-permissions-epoch'] as
          | string
          | undefined;
        if (epochHeader) {
          try {
            const key = 'permissions-epoch';
            const stored = window.localStorage.getItem(key);
            if (stored !== epochHeader) {
              window.localStorage.setItem(key, epochHeader);
              // Always dispatch — even on the very first hit after a
              // deploy. Sessions that pre-existed the interceptor
              // have `stored === null` but their persisted Zustand
              // `menu` cache is already populated from an older
              // /menu/user call, so we still need to force a refetch
              // to pick up server-side reorderings (e.g. migration
              // 0471/0481 pinning configuration to menu_order 9999).
              // The listener kills TanStack permission queries and
              // rewrites the menu store — extra work on a fresh
              // login is one throwaway request.
              window.dispatchEvent(new Event('auth:permissions-epoch-changed'));
            }
          } catch {
            /* localStorage disabled / SSR — ignore */
          }
        }
        return response.data;
      },
      async (error: unknown) => {
        const axiosError = error as {
          config: ExtendedAxiosRequestConfig &
            InternalAxiosRequestConfig & { _slotAcquired?: boolean };
          code?: string;
          message?: string;
          response?: {
            status?: number;
            data?: {
              errorCode?: string;
              data?: { branchOfficeId?: string | null };
            };
          };
        };
        const originalRequest = axiosError.config;
        // Release the concurrency slot on error too — otherwise a
        // failed GET keeps its slot forever and the queue drains
        // down to a stall.
        if (originalRequest?._slotAcquired) {
          this.releaseSlot();
          originalRequest._slotAcquired = false;
        }

        const requestUrl = String(originalRequest?.url || '');
        const isAuthRequest = this.isAuthEndpoint(requestUrl);

        // ECF endpoints may return 401 for business-level errors (e.g. no certificate,
        // DGII auth failed). These are NOT session expiry — do not trigger refresh/logout.
        const isEcfBusinessError =
          axiosError.response?.status === 401 && this.isEcfEndpoint(requestUrl);

        if (isEcfBusinessError) {
          return Promise.reject(this.createApiError(error));
        }

        // Transient gateway errors during a Railway deploy swap
        // (502 / 503 / 504) → retry with exponential backoff instead
        // of surfacing a hard failure. Covers the ~30-45 s window
        // where the new container is booting + the old one is being
        // torn down. Idempotent methods only (GET/HEAD/OPTIONS/PUT/
        // DELETE) — POST/PATCH are skipped so a duplicate charge or
        // stock movement never lands twice. Auth endpoints skip too
        // because their retry semantics are already owned by the
        // silent-refresh flow below.
        const transientStatuses = new Set([502, 503, 504]);
        const isTransient =
          !!axiosError.response?.status &&
          transientStatuses.has(axiosError.response.status);
        const method = String(originalRequest?.method ?? 'get').toLowerCase();
        const isIdempotent = [
          'get',
          'head',
          'options',
          'put',
          'delete',
        ].includes(method);
        const retryable = originalRequest as ExtendedAxiosRequestConfig & {
          _deployRetryCount?: number;
        };
        if (
          isTransient &&
          isIdempotent &&
          !isAuthRequest &&
          (retryable._deployRetryCount ?? 0) < 4
        ) {
          retryable._deployRetryCount = (retryable._deployRetryCount ?? 0) + 1;
          // 500ms → 1s → 2s → 4s. Total ~7.5 s worst case; fits
          // inside a Railway container swap without letting a real
          // upstream outage hang the UI forever.
          const delay = 500 * Math.pow(2, retryable._deployRetryCount - 1);
          await new Promise((r) => setTimeout(r, delay));
          return this.axiosInstance.request(originalRequest);
        }

        // If we already retried once after a refresh and still get 401,
        // the session is no longer valid (e.g., DB reset, user deleted).
        // Force logout to clear stale tokens.
        if (
          axiosError.response?.status === 401 &&
          originalRequest._retry &&
          !isAuthRequest
        ) {
          this.forceLogout();
          return this.rejectAuthExpiry(error);
        }

        const is401 =
          axiosError.response?.status === 401 &&
          !originalRequest._retry &&
          !isAuthRequest;

        // Handle 401 Unauthorized — attempt silent refresh before logging out
        if (is401) {
          console.warn('[api.service:401] silent refresh start', {
            url: originalRequest?.url,
            pathname:
              typeof window !== 'undefined' ? window.location.pathname : null,
            isImpersonating: useImpersonationStore.getState().isImpersonating,
          });
          originalRequest._retry = true;

          const { refreshToken } = useAuthStore.getState();

          // No refresh token available — force logout immediately
          if (!refreshToken) {
            this.forceLogout();
            return this.rejectAuthExpiry(error);
          }

          // Stale 401: the request left with an OLDER access token and this
          // tab has since rotated/adopted a newer one (a sibling request's
          // refresh already finished, or another tab rotated). Retry with the
          // current token — refreshing again would burn a second refresh-token
          // generation, and a sibling tab still holding the superseded refresh
          // token then trips the backend's revoke-on-reuse (e2e
          // multi-tab-refresh: 2× /auth/refresh, a tab never re-authed).
          const sentToken = this.getBearerToken(originalRequest);
          const currentToken = useAuthStore.getState().token;
          if (sentToken && currentToken && sentToken !== currentToken) {
            this.setBearerToken(originalRequest, currentToken);
            return this.axiosInstance(originalRequest);
          }

          // If a refresh is already in flight, queue this request
          if (this.isRefreshing) {
            return new Promise<string>((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            }).then((newToken) => {
              this.setBearerToken(originalRequest, newToken);
              return this.axiosInstance(originalRequest);
            });
          }

          this.isRefreshing = true;

          try {
            // Route the actual refresh through the cross-tab coordinator so at
            // most one `/auth/refresh` is issued per refresh-token generation
            // across all tabs of the origin. `'rotated'` = this tab refreshed;
            // `'adopted'` = another tab already rotated and we adopted its
            // credentials (no network); `'failed'` = genuine failure. On
            // rotated/adopted the auth store already carries the current token
            // (committed atomically via `setSession` inside the coordinator),
            // so we retry the queued + original requests with it.
            const outcome = await coordinatedRefresh();
            if (outcome === 'failed') {
              throw new Error('coordinated-refresh-failed');
            }
            const newAccessToken = useAuthStore.getState().token;
            this.processQueue(null, newAccessToken);
            this.setBearerToken(originalRequest, newAccessToken);
            return this.axiosInstance(originalRequest);
          } catch (refreshError) {
            // Refresh failed. During impersonation the sadmin's own
            // session is still valid — restore it so the sadmin
            // isn't wiped, BUT never retry the original impersonated
            // request under the sadmin token. The original request
            // was routed at an endpoint that gates on the
            // impersonated user's role (e.g. `/subscription/me/*`
            // which requires role='admin'); replaying it with the
            // sadmin JWT hits `assertBusinessAdmin` → 403 and leaves
            // the operator staring at a broken /account page with
            // the sadmin identity in the store. Force a hard-nav to
            // /sadmin/businesses instead — the operator lands on a
            // clean sadmin surface with no cross-identity request
            // hanging in flight. See feedback_proof_on_issue_resolution
            // .md (chained impersonation → 403 leak, 2026-08-14).
            this.processQueue(refreshError, null);
            if (this.tryRestoreImpersonator()) {
              this.hardNavigateToSadminHome();
              return this.rejectAuthExpiry(error);
            }
            this.forceLogout();
            return this.rejectAuthExpiry(error);
          } finally {
            this.isRefreshing = false;
          }
        }

        // Handle 403 Forbidden
        if (axiosError.response?.status === 403) {
          console.warn('[api.service:403]', {
            url: originalRequest?.url,
            method: originalRequest?.method,
            pathname:
              typeof window !== 'undefined' ? window.location.pathname : null,
            authRole: (() => {
              const r = useAuthStore.getState().user?.role as
                | { name?: string }
                | string
                | undefined;
              return typeof r === 'string' ? r : r?.name ?? null;
            })(),
            authUserId: useAuthStore.getState().user?.id ?? null,
            isImpersonating: useImpersonationStore.getState().isImpersonating,
            impersonationOriginalUserId:
              (
                useImpersonationStore.getState().originalUser as unknown as {
                  id?: string;
                }
              )?.id ?? null,
            errorCode: axiosError.response?.data?.errorCode,
          });
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('auth:forbidden'));
          }
        }

        // Retry logic
        if (this.shouldRetry(error, originalRequest)) {
          return this.retryRequest(originalRequest);
        }

        const apiError = this.createApiError(error);

        // Breadcrumb every failing request so any Sentry issue captured
        // afterwards carries the API failure trail in its timeline. 4xx
        // stays a breadcrumb; only 5xx and network errors surface as
        // captured events (avoids drowning the free-tier issue count
        // with expected auth denials + validation errors).
        addBreadcrumb({
          category: 'api',
          type: 'http',
          level:
            apiError.status && apiError.status >= 500 ? 'error' : 'warning',
          data: {
            method: String(originalRequest?.method || '').toUpperCase(),
            url: String(originalRequest?.url || ''),
            status_code: apiError.status,
            error_code: apiError.errorCode,
          },
        });

        // Centralized toast for trial expiration on GET requests.
        // Reports are query-heavy and often don't surface errors via UI.
        if (
          apiError.errorCode === 'TRIAL_EXPIRED' &&
          String(originalRequest?.method || '').toLowerCase() === 'get'
        ) {
          toastErrorOnce(
            `TRIAL_EXPIRED:${String(originalRequest?.url || '')}`,
            apiError.message
          );
        }

        // Centralized quota gate. Any 402 / *_QUOTA_EXCEEDED /
        // PLAN_LIMIT_* error pops the shared upgrade modal so every
        // surface (KDS screens, users, branches, warehouses, products,
        // …) gets the same UX. Call sites still get the rejection so
        // they can no-op their own toast.
        //
        // Subscription-lifecycle errors also return HTTP 402
        // (`SUBSCRIPTION_PAST_DUE`, `SUBSCRIPTION_SUSPENDED` from
        // SubscriptionStateInterceptor) but represent a DIFFERENT
        // problem — the tenant needs to add a payment method, not
        // upgrade a plan. Firing the "Alcanzaste tu capacidad"
        // paywall on those errors is user-hostile: the operator
        // sees a quota modal on a background hydration query the
        // moment they log in (or accept the T&C contract), even
        // though they haven't tried to breach any quota. The
        // SubscriptionBanner already carries the right CTA
        // ("Agregar método de pago") for that class.
        const status = apiError.status;
        const code = apiError.errorCode ?? '';
        const isLifecycleError =
          code === 'SUBSCRIPTION_PAST_DUE' ||
          code === 'SUBSCRIPTION_SUSPENDED' ||
          code === 'SUBSCRIPTION_INVOICE_OVERDUE';
        if (
          !isLifecycleError &&
          (status === 402 ||
            code.endsWith('QUOTA_EXCEEDED') ||
            code.startsWith('PLAN_LIMIT_'))
        ) {
          useQuotaErrorStore.getState().open(apiError);
        }

        // Centralized toast for subscription-lifecycle rejections.
        // Backend blocks WRITE calls (POST/PUT/PATCH/DELETE) via
        // SubscriptionStateInterceptor and returns HTTP 402 +
        // SUBSCRIPTION_SUSPENDED / SUBSCRIPTION_PAST_DUE. Mutation
        // hooks vary in their onError handling — many surface a
        // generic "Error al crear X" without translating the
        // errorCode. Firing one dedup'd toast here guarantees the
        // operator always sees the same "cuenta suspendida" message
        // regardless of which surface triggered the write.
        // apiError.message is already translated upstream via
        // errorCodeMap + i18next (see createApiError below).
        if (isLifecycleError && apiError.message) {
          // Dedup by errorCode ONLY — two concurrent requests to
          // different URLs (quotes page fires list + detail in
          // parallel) would otherwise both fire the same toast.
          // Lifecycle state is global, one message per burst.
          toastErrorOnce(code, apiError.message);
          // Silence the mutation hook's follow-up toasts (generic
          // "Error al crear Clientes" AND context-specific
          // `.custom(t(COMMON.ERROR))` fallbacks) — the operator
          // should see only the translated lifecycle message.
          suppressGenericErrorToasts();
        }

        // The selected branch is outside the caller's access set (revoked
        // mid-session, or a stale persisted selection). Never retry: drop
        // the branch and fall back to the first reachable one, once.
        if (code === ErrorCode.BRANCH_ACCESS_DENIED) {
          // The server names the denied branch; `null` = no branch to drop
          // (malformed id, or an empty access set). Older servers omit it —
          // then it was the header we sent.
          const denied = axiosError.response?.data?.data?.branchOfficeId;
          recoverFromBranchAccessDenied(
            denied === null
              ? undefined
              : denied ??
                  (originalRequest?.headers?.['X-Branch-Office-Id'] as
                    | string
                    | undefined)
          );
          if (apiError.message) toastErrorOnce(code, apiError.message);
          suppressGenericErrorToasts();
        }

        return Promise.reject(apiError);
      }
    );
  }

  /**
   * Endpoints that mutate critical state and are NOT safe to retry
   * automatically — every retry would double-create the resource.
   * Includes onboarding, addon purchase, subscription change, claim
   * submit, etc. If the call times out the user should see the
   * error and decide manually whether to retry from the UI.
   */
  private isNonRetryableEndpoint(url?: string): boolean {
    if (!url) return false;
    const u = url.startsWith('/') ? url : `/${url}`;
    return (
      u.includes('/auth/complete-onboarding') ||
      u.includes('/auth/register') ||
      u.includes('/auth/google') ||
      u.includes('/subscription/me/claims') ||
      u.includes('/subscription/me/addons/purchase') ||
      u.includes('/subscription-changes/me')
    );
  }

  private shouldRetry(
    error: unknown,
    config: ExtendedAxiosRequestConfig
  ): boolean {
    if (this.isNonRetryableEndpoint(config.url)) return false;
    // Invoice-creating POSTs: never auto-retry. Retry-after-500 is the
    // dominant duplicate-invoice source in production. Backend
    // content-hash dedupe protects against duplicates ANYWAY, but two
    // in-flight POSTs still burn latency + a rare cross-modal race.
    // Force the operator to reissue explicitly from the UI.
    if (this.isInvoiceCreateEndpoint(config.url, config.method)) return false;
    return (
      !config._retryCount &&
      (this.defaultRetryConfig.retryCondition?.(error) ?? false)
    );
  }

  private async retryRequest(
    config: ExtendedAxiosRequestConfig
  ): Promise<AxiosResponse> {
    config._retryCount = config._retryCount || 0;

    if (config._retryCount >= this.defaultRetryConfig.retries) {
      return Promise.reject(
        this.createApiError({ message: 'Max retries exceeded' })
      );
    }

    config._retryCount++;

    const delay =
      this.defaultRetryConfig.retryDelay * Math.pow(2, config._retryCount - 1);
    await new Promise((resolve) => setTimeout(resolve, delay));

    return this.axiosInstance(config);
  }

  /**
   * Resolve or reject all queued requests waiting for a token refresh.
   */
  private processQueue(error: unknown, token: string | null): void {
    this.failedQueue.forEach((promise) => {
      if (error) {
        promise.reject(error);
      } else if (token) {
        promise.resolve(token);
      } else {
        promise.reject(new Error('Missing access token after refresh'));
      }
    });
    this.failedQueue = [];
  }

  /**
   * Hard-navigate to `/sadmin/businesses` after an impersonation
   * restore. Every caller that swaps the sadmin session back onto
   * the auth store MUST invoke this — otherwise the operator stays
   * on the impersonated URL (`/account`, `/invoices`, `/pos`) with
   * a sadmin JWT, every subsequent query hits an admin-only guard
   * with the wrong role, and the FE shows sadmin data on a business
   * screen. A full window.location swap tears down any in-flight
   * impersonated request + resets the React tree cleanly.
   */
  private hardNavigateToSadminHome(): void {
    if (typeof window === 'undefined') return;
    if (window.location.pathname.startsWith('/sadmin/')) {
      console.log('[api.service:hardNav] already on sadmin surface, skipping', {
        pathname: window.location.pathname,
      });
      return;
    }

    console.log('[api.service:hardNav] navigating to /sadmin/businesses', {
      from: window.location.pathname,
    });
    window.location.replace('/sadmin/businesses');
  }

  /**
   * If sadmin was impersonating, swap back to sadmin's own tokens
   * instead of wiping the session. Returns true when the restore
   * happened so callers can skip the destructive logout path.
   * Impersonation store is persisted, so this works after F5 too.
   *
   * IMPORTANT: callers that resume execution (retry the original
   * request) after this returns true MUST call
   * `hardNavigateToSadminHome()` first — never replay an
   * impersonated URL's request under the restored sadmin token.
   */
  private tryRestoreImpersonator(): boolean {
    const impersonation = useImpersonationStore.getState();
    if (
      !impersonation.isImpersonating ||
      !impersonation.originalToken ||
      !impersonation.originalRefreshToken ||
      !impersonation.originalUser
    ) {
      console.log('[api.service:restoreImpersonator] skipped', {
        isImpersonating: impersonation.isImpersonating,
        hasOriginalToken: !!impersonation.originalToken,
        hasOriginalRefresh: !!impersonation.originalRefreshToken,
        hasOriginalUser: !!impersonation.originalUser,
      });
      return false;
    }

    console.warn(
      '[api.service:restoreImpersonator] swapping sadmin session back onto auth store',
      {
        pathname:
          typeof window !== 'undefined' ? window.location.pathname : null,
        originalUserId:
          (impersonation.originalUser as unknown as { id?: string })?.id ??
          null,
      }
    );
    // Batched — token / refreshToken / user always change together.
    // Prevents any subscriber from seeing a half-swapped (new token,
    // old user) frame. Rule: "Batch coupled React state" in
    // apps/neocrm/CLAUDE.md.
    useAuthStore.getState().setSession({
      token: impersonation.originalToken,
      refreshToken: impersonation.originalRefreshToken,
      user: impersonation.originalUser,
    });
    impersonation.endImpersonation();
    scheduleTokenRefresh(impersonation.originalToken);
    return true;
  }

  /**
   * Clear tokens, cancel proactive refresh timer, reset stores and fire event.
   * Skips the wipe when the caller is mid-impersonation — sadmin's
   * session is still valid and gets restored instead. Prevents the
   * "F5 during impersonation kicks sadmin to /login" bug.
   */
  private forceLogout(): void {
    if (this.tryRestoreImpersonator()) {
      this.hardNavigateToSadminHome();
      return;
    }
    clearTokenRefresh();
    useAuthStore.getState().logout();

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
  }

  /**
   * Sync auth state across browser tabs.
   * When another tab clears localStorage (logout) or updates the auth store,
   * this tab reacts accordingly.
   */
  private setupMultiTabSync(): void {
    if (typeof window === 'undefined') return;

    // Primary propagation: a signal-only BroadcastChannel('auth') message
    // ({type:'auth-rotated'}) → adopt the canonical rotated session. The token
    // is NEVER carried on the channel; the receiver re-reads canonical state.
    subscribeToAuthRotations(() => {
      void adoptRotatedSession();
    });

    // Fallback propagation: the `storage` event. Route adoption through the ONE
    // canonical `adoptRotatedSession()` (re-read canonical, setSession, re-arm
    // ONCE). NEVER trigger a refresh here and NEVER logout on a token *change*
    // (only an explicit clear logs out) — the old behavior re-armed the timer
    // and amplified the cross-tab refresh race.
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key !== 'auth') return;

      if (!event.newValue) {
        // localStorage was cleared — another tab genuinely logged out.
        clearTokenRefresh();
        useAuthStore.getState().logout();
        return;
      }

      try {
        const parsed = JSON.parse(event.newValue);
        const newToken = parsed?.state?.token;

        if (newToken && newToken !== useAuthStore.getState().token) {
          // Another tab rotated — adopt, do NOT refresh, do NOT logout.
          void adoptRotatedSession();
          return;
        }

        // Token explicitly emptied while we still think we're authenticated —
        // a logout from another tab.
        if (!newToken && useAuthStore.getState().isAuthenticated) {
          clearTokenRefresh();
          useAuthStore.getState().logout();
        }
      } catch {
        // Ignore parse errors
      }
    });
  }

  // HTTP Methods
  async get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.axiosInstance.get<T>(url, config);
      return response as T;
    } catch (error) {
      throw this.createApiError(error);
    }
  }

  async post<T = unknown>(
    url: string,
    data?: Record<string, unknown> | FormData,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      const response = await this.axiosInstance.post<T>(url, data, config);
      return response as T;
    } catch (error) {
      throw this.createApiError(error);
    }
  }

  async put<T = unknown>(
    url: string,
    data?: Record<string, unknown>,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      const response = await this.axiosInstance.put<T>(url, data, config);
      return response as T;
    } catch (error) {
      throw this.createApiError(error);
    }
  }

  async patch<T = unknown>(
    url: string,
    data?: Record<string, unknown>,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      const response = await this.axiosInstance.patch<T>(url, data, config);
      return response as T;
    } catch (error) {
      throw this.createApiError(error);
    }
  }

  async delete<T = unknown>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      const response = await this.axiosInstance.delete<T>(url, config);
      return response as T;
    } catch (error) {
      throw this.createApiError(error);
    }
  }

  // File upload with progress tracking
  async uploadFile<T = unknown>(
    url: string,
    file: File,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);

    const uploadConfig: AxiosRequestConfig = {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    };

    try {
      const response = await this.axiosInstance.post<T>(
        url,
        formData,
        uploadConfig
      );
      return response as T;
    } catch (error) {
      throw this.createApiError(error);
    }
  }

  // Multiple file upload
  async uploadFiles<T = unknown>(
    url: string,
    files: File[],
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append(`files[${index}]`, file);
    });

    const uploadConfig: AxiosRequestConfig = {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    };

    try {
      const response = await this.axiosInstance.post<T>(
        url,
        formData,
        uploadConfig
      );
      return response as T;
    } catch (error) {
      throw this.createApiError(error);
    }
  }

  // Download file
  async downloadFile(
    url: string,
    filename?: string,
    config?: AxiosRequestConfig
  ): Promise<void> {
    try {
      // The response interceptor unwraps `response.data` for every
      // request, so `axiosInstance.get(...)` here resolves directly
      // to the Blob — NOT to an AxiosResponse. Treat it as such;
      // dereferencing `response.data` writes "undefined" to the file.
      const blobOrData = (await this.axiosInstance.get(url, {
        ...config,
        responseType: 'blob',
      })) as unknown as Blob | ArrayBuffer | string;

      const blob =
        blobOrData instanceof Blob ? blobOrData : new Blob([blobOrData]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      throw this.createApiError(error);
    }
  }

  // Paginated requests
  async getPaginated<T = unknown>(
    url: string,
    params?: { page?: number; limit?: number; [key: string]: unknown },
    config?: AxiosRequestConfig
  ): Promise<PaginatedResponse<T>> {
    const queryParams = {
      page: 1,
      limit: 10,
      ...params,
    };

    try {
      const response = await this.axiosInstance.get<PaginatedResponse<T>>(url, {
        ...config,
        params: queryParams,
      });
      return response.data;
    } catch (error) {
      throw this.createApiError(error);
    }
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    try {
      return await this.get('/health');
    } catch (error) {
      throw this.createApiError(error);
    }
  }

  // Token management
  setAuthToken(token: string): void {
    useAuthStore.getState().setToken(token);
    this.axiosInstance.defaults.headers.common[
      'Authorization'
    ] = `Bearer ${token}`;
  }

  removeAuthToken(): void {
    useAuthStore.getState().logout();
    delete this.axiosInstance.defaults.headers.common['Authorization'];
  }

  // Configuration
  setBaseURL(baseURL: string): void {
    this.axiosInstance.defaults.baseURL = baseURL;
  }

  setTimeout(timeout: number): void {
    this.axiosInstance.defaults.timeout = timeout;
  }

  // Error handling
  private createApiError(error: unknown): ApiError {
    // Return a REAL Error instance (not a plain object literal) so that any
    // unhandled rejection of an ApiError lands in Sentry as a grouped Error
    // with a stack, instead of the ungroupable, stackless
    // "Object captured as promise rejection with keys: code, data, details,
    // errorCode, message, status" (Sentry NEOWEB-4). The ApiError interface
    // fields are attached as enumerable own props, so every reader
    // (getTranslatedErrorMessage, errorCodeMap lookups, isApiError duck-type,
    // `{ ...err }` spreads of the context fields) keeps working unchanged.
    const apiError = new Error('An unknown error occurred') as Error & ApiError;
    apiError.name = 'ApiError';
    apiError.status = undefined;
    apiError.code = undefined;
    apiError.errorCode = undefined;
    apiError.details = undefined;
    apiError.data = undefined;

    const axiosError = error as {
      response?: {
        status?: number;
        statusText?: string;
        data?: {
          message?: string;
          error?: string;
          errorCode?: string;
          data?: Record<string, unknown>;
          [key: string]: unknown;
        };
      };
      request?: unknown;
      message?: string;
    };

    // Cancellation check runs BEFORE the response / request branches
    // because an aborted request may still carry a partial `.request`
    // handle (particularly when the interceptor cancels pre-flight while
    // the account is locked or the query is unmounted mid-flight). If
    // we let the `.request` branch match first, cancels get mislabeled
    // as NETWORK_ERROR and the retry logic downstream fires for what
    // was an intentional cancel.
    const isCanceled =
      (axiosError as { code?: string }).code === 'ERR_CANCELED' ||
      (axiosError as { name?: string }).name === 'CanceledError';
    if (isCanceled) {
      apiError.message = 'Request aborted';
      apiError.code = 'NETWORK_ABORTED';
    } else if (axiosError.response) {
      apiError.status = axiosError.response.status;
      apiError.message =
        axiosError.response.data?.message ||
        axiosError.response.data?.error ||
        `HTTP ${axiosError.response.status}: ${axiosError.response.statusText}`;
      apiError.details = axiosError.response.data as Record<string, unknown>;

      // Extract 'data' field from backend response (contains onboardingToken, etc.)
      if (axiosError.response.data?.data) {
        apiError.data = axiosError.response.data.data as Record<
          string,
          unknown
        >;
      }

      // Extract errorCode from backend response for translation mapping
      const backendErrorCode = axiosError.response.data?.errorCode;
      if (backendErrorCode) {
        apiError.code = backendErrorCode;
        apiError.errorCode = backendErrorCode;
      } else {
        // Fallback to generic HTTP status codes
        switch (axiosError.response.status) {
          case 400:
            apiError.code = 'BAD_REQUEST';
            apiError.errorCode = 'BAD_REQUEST';
            break;
          case 401:
            apiError.code = 'UNAUTHORIZED';
            apiError.errorCode = 'UNAUTHORIZED';
            break;
          case 403:
            apiError.code = 'FORBIDDEN';
            apiError.errorCode = 'FORBIDDEN';
            break;
          case 404:
            apiError.code = 'NOT_FOUND';
            apiError.errorCode = 'NOT_FOUND';
            break;
          case 422:
            apiError.code = 'VALIDATION_ERROR';
            break;
          case 429:
            apiError.code = 'RATE_LIMITED';
            break;
          case 500:
            apiError.code = 'INTERNAL_SERVER_ERROR';
            apiError.errorCode = 'INTERNAL_SERVER_ERROR';
            break;
          default:
            apiError.code = 'HTTP_ERROR';
        }
      }

      // If we have a known errorCode mapping, localize the message centrally.
      // This ensures callers that display `error.message` (toasts, forms, etc.)
      // get consistent translated messages without per-endpoint handling.
      const mappedMsg = apiError.errorCode
        ? errorCodeMap[apiError.errorCode as keyof typeof errorCodeMap]
        : undefined;
      if (mappedMsg) {
        try {
          apiError.message = i18next.t(mappedMsg);
        } catch {
          // If i18n isn't initialized for any reason, fall back to backend message.
        }
      }
    } else if (axiosError.request) {
      apiError.message = 'Network error. Please check your connection.';
      apiError.code = 'NETWORK_ERROR';
    } else {
      apiError.message = axiosError.message || apiError.message;
      apiError.code = 'UNKNOWN_ERROR';
    }

    // Cancellations are load-bearing on this app — React Query cancels
    // stale queries on refocus / unmount, the request interceptor
    // aborts every non-unlock request while locked, and the SUSPENDED
    // gate remounts the tree mid-flight. Logging every abort as a red
    // 🔥 error floods the console + hides real errors during triage.
    // Downgrade cancellations to debug (visible only when the operator
    // opens DevTools with verbose enabled) and keep the loud log for
    // genuine errors.
    if (process.env.NODE_ENV === 'development') {
      if (apiError.code === 'NETWORK_ABORTED') {
        console.debug(`🟡 API Cancel [${apiError.code}]: ${apiError.message}`);
      } else {
        console.error(
          `🔥 API Error [${apiError.code ?? 'UNKNOWN'}]: ${apiError.message}`,
          apiError.details ?? apiError
        );
      }
    }

    return apiError;
  }

  /**
   * Reject with an ApiError flagged as an EXPECTED session expiry. Used on the
   * 401 paths that have already called `forceLogout()` (no refresh token /
   * refresh failed / impersonation restored). The user is being logged out on
   * purpose, so the resulting rejection — even if a background caller never
   * `.catch`es it — is not a bug. Sentry's `beforeSend` drops anything carrying
   * `isAuthSessionExpired`, which kills the NEOWEB-4 unhandled-rejection noise
   * without hiding genuine errors.
   */
  private rejectAuthExpiry(error: unknown): Promise<never> {
    const authErr = this.createApiError(error) as ApiError & {
      isAuthSessionExpired?: boolean;
    };
    authErr.isAuthSessionExpired = true;
    return Promise.reject(authErr);
  }

  // Utility methods
  static isApiError(error: unknown): error is ApiError {
    return (
      typeof error === 'object' &&
      error !== null &&
      'message' in error &&
      typeof (error as { message: unknown }).message === 'string' &&
      'code' in error
    );
  }

  createCancelToken() {
    return axios.CancelToken.source();
  }

  getInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;
